import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";

import { Check, Quote, Star } from "lucide-react";
import { Footer, Header, QuoteForm, PAGE_CONTAINER } from "../components/site";
import { HomeHero } from "../components/home-hero";
import { InfoBanners } from "../components/info-banners";
import { Dienstkaarten } from "../components/dienstkaarten";
import { SITE } from "../lib/seo-data";
import { useTaal } from "../lib/taal";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "offerto - elektricien offertes vergelijken, gratis & vrijblijvend" },
      {
        name: "description",
        content:
          "Vergelijk offertes van erkende elektriciens bij jou in de buurt. Eén aanvraag, meerdere prijzen. Gratis, vrijblijvend en binnen 1 minuut geregeld.",
      },
      { property: "og:title", content: "offerto - elektricien offertes vergelijken, gratis & vrijblijvend" },
      {
        property: "og:description",
        content:
          "Eén aanvraag, meerdere prijzen van erkende elektriciens bij jou in de buurt. Bespaar tot 40%.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}/` },
      { property: "og:locale", content: "nl_NL" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/` },
    ],
  }),
  component: Index,
});

function Index() {
  const { t } = useTaal();

  useEffect(() => {
    // A restored mobile scroll position can leave the title behind the sticky header.
    // Only reset when returning to the top of Home, not when browsing further down.
    const resetCoveredTitle = () => {
      const title = document.querySelector("main h1, section h1");
      const header = document.querySelector("header");
      if (title && header && window.scrollY < 250 && title.getBoundingClientRect().top < header.getBoundingClientRect().bottom) {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      }
    };

    requestAnimationFrame(resetCoveredTitle);
    window.addEventListener("pageshow", resetCoveredTitle);
    return () => window.removeEventListener("pageshow", resetCoveredTitle);
  }, []);

  const reviews = [
    { q: t("review1"), naam: "Frans J.", plaats: "Utrecht", initialen: "FJ" },
    { q: t("review2"), naam: "X. Schouten", plaats: "Breda", initialen: "XS" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <HomeHero />


      <Dienstkaarten />

      <InfoBanners />

      <section className="bg-white py-16">
        <div className={PAGE_CONTAINER}>
          <p className="text-[13px] font-bold uppercase tracking-[0.08em] text-[#4A5468]">{t("reviewsEyebrow")}</p>
          <h2 className="mt-3 text-4xl font-extrabold tracking-tight">{t("reviewsTitel")}</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-[300px_minmax(0,1fr)_minmax(0,1fr)]">
            <div className="flex flex-col items-center rounded-2xl bg-[#14213D] p-6 text-center text-white md:col-span-2 lg:col-span-1">
              <div className="flex gap-1" aria-hidden="true">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="size-5 fill-[#F2B705] text-[#F2B705]" />
                ))}
              </div>
              <p className="mt-4 text-[56px] font-extrabold leading-none tracking-tight">{t("reviewsScore")}</p>
              <p className="mt-3 text-sm text-[#C3CBDB]">{t("heroReviews")}</p>
            </div>
            {reviews.map((r) => (
              <blockquote key={r.naam} className="flex flex-col rounded-2xl border border-[#E3E6EB] bg-[#F4F5F7] p-6">
                <Quote className="size-7 fill-hero-accent text-hero-accent" aria-hidden="true" />
                <p className="mt-4 text-[19px] font-medium leading-relaxed">“{r.q}”</p>
                <footer className="mt-auto flex items-center gap-3 pt-6">
                  <span className="flex size-10 items-center justify-center rounded-full bg-[#14213D] text-sm font-bold text-white">{r.initialen}</span>
                  <span>
                    <span className="block text-sm font-bold">{r.naam}</span>
                    <span className="block text-sm text-[#4A5468]">{r.plaats}</span>
                  </span>
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      <section className={`${PAGE_CONTAINER} pb-16`}>
        <div className="grid items-center gap-10 rounded-3xl bg-[#14213D] px-6 py-12 sm:px-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          <div>
            <h2 className="text-[38px] font-extrabold leading-tight tracking-tight text-white">{t("ctaTitel")}</h2>
            <p className="mt-3 text-[#C3CBDB]">{t("ctaTekst")}</p>
          </div>
          <div>
            <QuoteForm hero />
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
              {[t("ctaVink1"), t("ctaVink2")].map((v) => (
                <span key={v} className="flex items-center gap-2 text-sm font-semibold text-white">
                  <Check className="size-4 text-hero-accent" aria-hidden="true" />
                  {v}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
